/**
 * Header Component
 *
 * Displays main navigation, branding, and profile actions.
 * Supports:
 *  - Desktop navigation
 *  - Mobile hamburger menu with collapsible items
 *  - Profile dropdown menu
 */

import React, { useState } from "react";
import "../../styles/header.css";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useUnsavedChanges } from "../editor/UnsavedChangesContext";
import { apiClient } from "../../services/api";

const AUTH_KEYS = [
  "authenticated",
  "userId",
  "session_token",
  "userName",
  "userEmail",
];

const hydrateSessionStorage = () => {
  AUTH_KEYS.forEach((key) => {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (!sessionValue && localValue) {
      sessionStorage.setItem(key, localValue);
    }
  });
};

const getAuthItem = (key) => {
  return sessionStorage.getItem(key) || localStorage.getItem(key);
};

const Header = () => {
  hydrateSessionStorage();

  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated =
    getAuthItem("authenticated") === "true" ||
    Boolean(getAuthItem("session_token"));

  const { hasUnsavedChanges, setHasUnsavedChanges, invokeSaveCallback } =
    useUnsavedChanges();

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const getNavButtonClass = (path) => {
    const isActive = location.pathname.startsWith(path);

    return `inline-flex items-center justify-center whitespace-nowrap px-3 lg:px-5 py-2 rounded-md font-medium transition border ${
      isActive
        ? "bg-[#0284C7] text-white border-black"
        : "bg-[#0EA5E9] text-white border-transparent hover:bg-[#0284C7]"
    }`;
  };

  const handleLogoutClick = async (e) => {
    e.preventDefault();
    setProfileMenuOpen(false);

    if (hasUnsavedChanges) {
      const result = await Swal.fire({
        title: "Unsaved Changes",
        text: "There are unsaved changes in your project. Would you like to save them or discard?",
        icon: "warning",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Save",
        denyButtonText: "Discard",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        denyButtonColor: "#d33",
      });

      if (result.isConfirmed) {
        try {
          await invokeSaveCallback();
          await Swal.fire({
            icon: "success",
            title: "Saved!",
            text: "Your work has been saved.",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          // Cancelling the popup isn't a failure — just stay logged in.
          if (!error?.cancelled) {
            console.error("Save failed:", error);
            await Swal.fire(
              "Error",
              "Could not save the file. Please try again.",
              "error"
            );
          }
          return;
        }

        navigate("/logout");
      } else if (result.isDenied) {
        const userId = getAuthItem("userId");
        const sessionToken = getAuthItem("session_token");
        const sessionIds = JSON.parse(
          sessionStorage.getItem("session_dataset_ids") || "[]"
        );

        for (const datasetId of sessionIds) {
          try {
            await apiClient.delete(
              `/api/storage/datasets/${datasetId}?userId=${userId}&session_token=${sessionToken}`
            );
          } catch (error) {
            console.error("Failed to delete session dataset:", datasetId, error);
          }
        }

        sessionStorage.removeItem("session_dataset_ids");
        setHasUnsavedChanges(false);
        navigate("/logout");
      } else {
        return;
      }
    } else {
      navigate("/logout");
    }
  };

  const handleNavClick = async (e, path) => {
    e.preventDefault();

    if (hasUnsavedChanges) {
      const result = await Swal.fire({
        title: "Unsaved Changes",
        text: "There are unsaved changes in your project. Would you like to save them or discard?",
        icon: "warning",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Save",
        denyButtonText: "Discard",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        denyButtonColor: "#d33",
      });

      if (result.isConfirmed) {
        try {
          await invokeSaveCallback();
        } catch (error) {
          // Cancelling the popup isn't a failure — just stay on the page.
          if (!error?.cancelled) {
            console.error("Save failed:", error);
            await Swal.fire(
              "Error",
              "Could not save the file. Please try again.",
              "error"
            );
          }
          return;
        }
      } else if (result.isDenied) {
        // Discard counts as a decision — don't ask again.
        setHasUnsavedChanges(false);
      } else {
        return;
      }
    }

    navigate(path);
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-[#E2E8F0] font-[Manrope]">
        <div className="w-full flex items-center justify-between px-4 md:px-8 py-5">
          {/* Left: Logo + Name + Tagline */}
          <div
            className="flex items-center gap-3 cursor-pointer rounded-lg px-2 py-1 transition hover:bg-[#E0F2FE] hover:scale-[1.02]"
            onClick={(e) => handleNavClick(e, isAuthenticated ? "/home" : "/")}
          >
            <img
              src="/deebug-logo-light.png"
              alt="DEEBug logo"
              className="h-16 w-auto"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-extrabold text-[#0EA5E9] tracking-wide">
                DEEBug
              </span>
              <span className="text-sm text-[#334155] font-medium">
                <strong>D</strong>etect.<strong>E</strong>xplain.
                <strong>E</strong>nhance.<strong>B</strong>ug
              </span>
            </div>
          </div>

          {/* Middle Navigation Desktop */}
          <ul
            role="navigation"
            aria-label="Main"
            className="hidden md:flex items-center gap-4 lg:gap-8 text-base lg:text-lg font-semibold"
          >
            {isAuthenticated && (
              <>
                <li>
                  <Link
                    to="/projects"
                    onClick={(e) => handleNavClick(e, "/projects")}
                    className={getNavButtonClass("/projects")}
                  >
                    Projects
                  </Link>
                </li>

                <li>
                  <Link
                    to="/codebase/new/data-preparation"
                    onClick={(e) =>
                      handleNavClick(e, "/codebase/new/data-preparation")
                    }
                    className={getNavButtonClass("/codebase")}
                  >
                    Code Editor
                  </Link>
                </li>
              </>
            )}

            <li>
              <Link
                to="/resources"
                onClick={(e) => handleNavClick(e, "/resources")}
                className={getNavButtonClass("/resources")}
              >
                Resources
              </Link>
            </li>
          </ul>

          {/* Right Side: Profile + Mobile Toggle */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen((prev) => !prev)}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-[#F1F5F9] transition"
                >
                  <span className="hidden md:block font-bold text-[#334155] text-sm">
                    {getAuthItem("userName") || "User"}
                  </span>

                  <div className="h-8 w-8 rounded-full border border-[#CBD5E1] overflow-hidden bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center shadow-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="white"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                      />
                    </svg>
                  </div>

                  <span className="hidden sm:inline text-[#334155] text-sm">
                    ▼
                  </span>
                </button>

                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 bg-white border border-[#E2E8F0] rounded-md shadow-lg w-40 text-sm z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 hover:bg-[#F1F5F9] text-[#334155]"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-[#F1F5F9] text-[#DC2626] font-medium"
                      onClick={handleLogoutClick}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <button
                  onClick={() => navigate("/login")}
                  className="text-[#0F172A] font-semibold hover:text-[#0284C7] transition"
                >
                  Sign in
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="border border-[#0EA5E9] text-[#0EA5E9] bg-white px-4 py-2 rounded-md font-semibold hover:bg-[#E0F2FE] transition"
                >
                  Sign up
                </button>
              </div>
            )}

            {/* Mobile Nav Toggle */}
            <button
              className="inline-flex items-center justify-center md:hidden p-2 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] focus:outline-none"
              onClick={() => setMobileNavOpen((prev) => !prev)}
              aria-label="Toggle navigation menu"
            >
              <svg
                className="h-6 w-6 text-[#0F172A]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileNavOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeWidth="1.8"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeWidth="1.8"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {mobileNavOpen && (
          <div className="md:hidden border-t border-[#E2E8F0] bg-white">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4 text-base font-semibold text-[#334155]">
              {isAuthenticated && (
                <>
                  <Link
                    to="/projects"
                    onClick={(e) => {
                      handleNavClick(e, "/projects");
                      setMobileNavOpen(false);
                    }}
                    className="hover:text-[#0EA5E9] transition"
                  >
                    Projects
                  </Link>

                  <Link
                    to="/codebase/new/data-preparation"
                    onClick={(e) => {
                      handleNavClick(e, "/codebase/new/data-preparation");
                      setMobileNavOpen(false);
                    }}
                    className="hover:text-[#0EA5E9] transition"
                  >
                    Code Editor
                  </Link>
                </>
              )}

              <Link
                to="/resources"
                onClick={(e) => {
                  handleNavClick(e, "/resources");
                  setMobileNavOpen(false);
                }}
                className="hover:text-[#0EA5E9] transition"
              >
                Resources
              </Link>

              {!isAuthenticated && (
                <>
                  <button
                    onClick={() => {
                      navigate("/login");
                      setMobileNavOpen(false);
                    }}
                    className="text-left text-[#0F172A] font-semibold text-base hover:text-[#0284C7] transition"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={() => {
                      navigate("/signup");
                      setMobileNavOpen(false);
                    }}
                    className="text-left border border-[#0EA5E9] text-[#0EA5E9] text-base px-4 py-2 rounded-md font-semibold hover:bg-[#E0F2FE] transition"
                  >
                    Sign up
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;